import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import type { ObservedUsage, ObservedUsageGroup, ObservedUsageRow, ObservedUsageWindow } from "../../model";
import { observedUsageWindowDefinition, observedUsageWindows } from "../../model";
import { errorMessage } from "../../api/errors";
import type { ObservedUsageController } from "../../state/observed-usage";
import { formatDate, formatExactInteger, formatNumber, formatPercent, formatRelativeTimestamp } from "../../utils/format";

interface ObservedUsagePanelProps {
  controller: ObservedUsageController;
  onSelectWindow: (window: ObservedUsageWindow) => void;
  onSelectGroup: (group: ObservedUsageGroup) => void;
}

type GroupedUsageRow = ObservedUsageRow & {
  subject: Exclude<ObservedUsageRow["subject"], { kind: "all" }>;
};

const groupLabels: Record<ObservedUsageGroup, string> = {
  all: "All",
  key: "By Key",
  relay: "By Relay",
};

export function ObservedUsagePanel(props: ObservedUsagePanelProps): JSX.Element {
  const [keySearch, setKeySearch] = createSignal("");
  const [relaySearch, setRelaySearch] = createSignal("");
  const [expandedSubject, setExpandedSubject] = createSignal<string | null>(null);
  const state = () => props.controller.state();
  const data = () => state().data ?? undefined;
  const row = () => data()?.groupBy === "all" ? data()!.rows[0] ?? null : null;
  const groupedRows = createMemo<GroupedUsageRow[]>(() => {
    const current = data();
    if (current?.groupBy === "key") {
      const needle = keySearch().trim().toLocaleLowerCase();
      const stateOrder = { active: 0, disabled: 1, revoked: 2 } as const;
      return current.rows
        .filter((item): item is ObservedUsageRow & { subject: Extract<ObservedUsageRow["subject"], { kind: "key" }> } => item.subject.kind === "key")
        .filter((item) => needle === "" || [item.subject.label, item.subject.publicID, item.subject.internalID, item.subject.state]
          .some((value) => value.toLocaleLowerCase().includes(needle)))
        .slice()
        .sort((left, right) => stateOrder[left.subject.state] - stateOrder[right.subject.state]
          || left.subject.label.localeCompare(right.subject.label)
          || left.subject.internalID.localeCompare(right.subject.internalID));
    }
    if (current?.groupBy === "relay") {
      const needle = relaySearch().trim().toLocaleLowerCase();
      return current.rows
        .filter((item): item is ObservedUsageRow & { subject: Extract<ObservedUsageRow["subject"], { kind: "relay" }> } => item.subject.kind === "relay")
        .filter((item) => needle === "" || [item.subject.displayName, item.subject.internalID, item.subject.state]
          .some((value) => value.toLocaleLowerCase().includes(needle)))
        .slice()
        .sort((left, right) => left.subject.displayName.localeCompare(right.subject.displayName, "zh-CN")
          || left.subject.internalID.localeCompare(right.subject.internalID));
    }
    return [];
  });

  return (
    <section class="bento bento--usage" aria-labelledby="observed-usage-heading">
      <header class="bento__header">
        <div>
          <p class="eyebrow">Observed Usage · 滚动 {observedUsageWindowDefinition(data()?.window ?? state().window)!.label} · {groupLabels[state().group]}</p>
          <h2 id="observed-usage-heading">观测用量</h2>
        </div>
        <div class="observed-usage__controls">
          <div class="segmented" role="group" aria-label="Observed Usage 分组维度">
            <For each={(["all", "key", "relay"] as const)}>{(group) => (
              <button
                type="button"
                class="segmented__button"
                aria-pressed={state().group === group}
                disabled={state().status === "unsupported" || state().group === group}
                onClick={() => props.onSelectGroup(group)}
              >
                {groupLabels[group]}
              </button>
            )}</For>
          </div>
          <div class="segmented" role="group" aria-label="Observed Usage 时间窗口">
            <For each={observedUsageWindows}>{(window) => (
              <button
                type="button"
                class="segmented__button"
                aria-pressed={state().window === window.value}
                disabled={state().status === "unsupported" || state().window === window.value}
                onClick={() => props.onSelectWindow(window.value)}
              >
                {window.label}
              </button>
            )}</For>
          </div>
          <button
            type="button"
            class="button button--ghost"
            disabled={state().status === "unsupported" || state().status === "loading" || state().status === "refreshing"}
            onClick={() => void props.controller.refresh()}
          >
            刷新当前
          </button>
          <p class="bento__hint">来自 Gateway 保留的 Hot Request History；不是账单或 Admission 权威数据。</p>
        </div>
      </header>

      <Show when={state().group === "all" && row()}>
        {(current) => <AllUsage row={current()} />}
      </Show>
      <Show when={state().group !== "all" && data()}>
        <GroupedUsageTable
          group={state().group === "key" ? "key" : "relay"}
          rows={groupedRows()}
          search={state().group === "key" ? keySearch() : relaySearch()}
          expandedSubject={expandedSubject()}
          onSearch={state().group === "key" ? setKeySearch : setRelaySearch}
          onToggle={(subject) => setExpandedSubject(expandedSubject() === subject ? null : subject)}
        />
      </Show>
      <Show when={data()}>
        {(current) => <HistoryStatus data={current()} />}
      </Show>
      <Show when={data() === undefined && (state().status === "idle" || state().status === "loading")}>
        <div class="skeleton-block" aria-label="正在加载 Observed Usage" />
      </Show>
      <UsageQueryState controller={props.controller} />
    </section>
  );
}

function UsageQueryState(props: { controller: ObservedUsageController }): JSX.Element {
  const state = () => props.controller.state();
  return (
    <div class="panel-state">
      <Show when={state().status === "unsupported"}>
        <p class="callout callout--neutral">当前 Gateway 不支持 Observed Usage；其余 Dashboard 功能仍可使用。</p>
      </Show>
      <Show when={state().status !== "unsupported"}>
        <span>{formatRelativeTimestamp(state().updatedAt)}</span>
      </Show>
      <Show when={state().status === "loading"}>
        <span class="inline-loading">正在加载</span>
      </Show>
      <Show when={state().status === "refreshing"}>
        <span class="inline-loading">正在刷新缓存数据</span>
      </Show>
      <Show when={state().status === "stale"}>
        <span class="panel-state__error">
          刷新失败，正在显示已过期缓存：{errorMessage(state().error)}
          <button type="button" class="text-button" onClick={() => void props.controller.refresh()}>重试</button>
        </span>
      </Show>
      <Show when={state().status === "error"}>
        <span class="panel-state__error">
          加载失败：{errorMessage(state().error)}
          <button type="button" class="text-button" onClick={() => void props.controller.refresh()}>重试</button>
        </span>
      </Show>
    </div>
  );
}

function AllUsage(props: { row: ObservedUsageRow }): JSX.Element {
  return (
    <>
      <div class="metric-row metric-row--usage">
        <div class="metric"><span>请求</span><strong>{formatNumber(props.row.requests.total)}</strong></div>
        <div class="metric"><span>Responses</span><strong>{formatNumber(props.row.requests.responses)}</strong></div>
        <div class="metric"><span>Search</span><strong>{formatNumber(props.row.requests.search)}</strong></div>
        <div class="metric"><span>完成</span><strong>{formatNumber(props.row.requests.completed)}</strong></div>
        <div class="metric"><span>异常结束</span><strong>{formatNumber(props.row.requests.abnormal)}</strong></div>
      </div>
      <div class="metric-row metric-row--usage">
        <div class="metric"><span>已上报 Usage</span><strong>{formatNumber(props.row.usage.reported)}</strong></div>
        <div class="metric"><span>Usage 缺失</span><strong>{formatNumber(props.row.usage.missing)}</strong></div>
        <div class="metric"><span>Usage 解析错误</span><strong>{formatNumber(props.row.usage.parseError)}</strong></div>
        <div class="metric"><span>Token Coverage</span><strong>{coverageText(props.row)}</strong></div>
        <div class="metric"><span>Total Token</span><strong>{formatExactInteger(props.row.tokens.total.sum)}</strong></div>
      </div>
      <details class="observed-usage__details">
        <summary>Provider-reported Token 明细</summary>
        <div class="usage-breakdown">
          <TokenBreakdown row={props.row} />
        </div>
      </details>
    </>
  );
}

function GroupedUsageTable(props: {
  group: "key" | "relay";
  rows: GroupedUsageRow[];
  search: string;
  expandedSubject: string | null;
  onSearch: (value: string) => void;
  onToggle: (subject: string) => void;
}): JSX.Element {
  const entityLabel = () => props.group === "key" ? "Gateway Key" : "Relay";
  return (
    <div class="observed-usage__group-view">
      <label class="field observed-usage__search">
        <span>搜索 {entityLabel()} 用量</span>
        <input
          type="search"
          aria-label={`搜索 ${entityLabel()} 用量`}
          value={props.search}
          placeholder={props.group === "key" ? "Label、Public ID、Internal ID 或 state" : "Internal ID、名称或 state"}
          onInput={(event) => props.onSearch(event.currentTarget.value)}
        />
      </label>
      <Show when={props.rows.length > 0} fallback={<p class="empty-state">没有匹配的 {entityLabel()}</p>}>
        <div class="table-scroll observed-usage__group-table">
          <table aria-label={`${groupLabels[props.group]} Observed Usage`}>
            <thead><tr><th>{entityLabel()}</th><th>State</th><th>Requests</th><th>Responses / Search</th><th>Usage completeness</th><th>Token Coverage</th><th>Total Token</th><th>Details</th></tr></thead>
            <tbody>
              <For each={props.rows}>{(item) => {
                const subjectKey = `${item.subject.kind}:${item.subject.internalID}`;
                return (
                  <>
                    <tr>
                      <td><span class="entity-link"><strong>{subjectName(item)}</strong><For each={subjectCodes(item)}>{(code) => <code>{code}</code>}</For></span></td>
                      <td><span class={`status-badge status-badge--${statusTone(item.subject.state)}`}>{item.subject.state}</span></td>
                      <td>{formatNumber(item.requests.total)}</td>
                      <td>{formatNumber(item.requests.responses)} / {formatNumber(item.requests.search)}</td>
                      <td>{formatNumber(item.usage.reported)} reported · {formatNumber(item.usage.missing)} missing · {formatNumber(item.usage.parseError)} parse error</td>
                      <td>{coverageText(item)}</td>
                      <td>{formatExactInteger(item.tokens.total.sum)}</td>
                      <td><button type="button" class="button button--ghost button--small" onClick={() => props.onToggle(subjectKey)}>Token 明细</button></td>
                    </tr>
                    <Show when={props.expandedSubject === subjectKey}>
                      <tr class="observed-usage__token-row">
                        <td colSpan={8}>
                          <strong>Provider-reported Token 明细</strong>
                          <div class="usage-breakdown">
                            <TokenBreakdown row={item} />
                          </div>
                        </td>
                      </tr>
                    </Show>
                  </>
                );
              }}</For>
            </tbody>
          </table>
        </div>
      </Show>
    </div>
  );
}

function TokenBreakdown(props: { row: ObservedUsageRow }): JSX.Element {
  return (
    <>
      <TokenValue label="Input" value={props.row.tokens.input.sum} known={props.row.tokens.input.knownRecords} />
      <TokenValue label="Output" value={props.row.tokens.output.sum} known={props.row.tokens.output.knownRecords} />
      <TokenValue label="Total" value={props.row.tokens.total.sum} known={props.row.tokens.total.knownRecords} />
      <TokenValue label="Cache read" value={props.row.tokens.cacheRead.sum} known={props.row.tokens.cacheRead.knownRecords} />
      <TokenValue label="Cache write" value={props.row.tokens.cacheWrite.sum} known={props.row.tokens.cacheWrite.knownRecords} />
      <span>Reported without total <strong>{formatNumber(props.row.usage.reportedWithoutTotal)}</strong></span>
    </>
  );
}

function subjectName(row: GroupedUsageRow): string {
  return row.subject.kind === "key" ? row.subject.label : row.subject.displayName;
}

function subjectCodes(row: GroupedUsageRow): string[] {
  return row.subject.kind === "key"
    ? [row.subject.publicID, row.subject.internalID]
    : [row.subject.internalID];
}

function statusTone(state: GroupedUsageRow["subject"]["state"]): "good" | "warning" | "danger" | "neutral" {
  if (state === "active") return "good";
  if (state === "disabled") return "warning";
  return state === "revoked" ? "danger" : "neutral";
}

function coverageText(row: ObservedUsageRow): string {
  return row.tokenCoverage === null
    ? "不适用"
    : `${formatNumber(row.tokenCoverage.known)} / ${formatNumber(row.tokenCoverage.eligible)} · ${formatPercent((row.tokenCoverage.known / row.tokenCoverage.eligible) * 100)}`;
}

function HistoryStatus(props: { data: ObservedUsage }): JSX.Element {
  return (
    <>
      <p class={`callout ${props.data.hotHistory.windowStatus === "partial" ? "callout--warning" : "callout--neutral"}`}>
        {props.data.hotHistory.windowStatus === "complete"
          ? "Hot Request History 未因 100,000 条上限截断；不代表 Meter queue、SQLite 写入或此前进程生命周期没有缺口。"
          : "Hot Request History 因 100,000 条保留上限截断了所选窗口；Observed Usage 仅包含仍保留的记录。"}
      </p>
      <p class="observed-usage__snapshot">
        Gateway 快照 {formatDate(props.data.generatedAtMS)} · Hot {formatNumber(props.data.hotHistory.count)} / {formatNumber(props.data.hotHistory.capacity)}
      </p>
    </>
  );
}

function TokenValue(props: { label: string; value: string | null; known: number }): JSX.Element {
  return (
    <span>
      {props.label} <strong>{formatExactInteger(props.value)}</strong> · {formatNumber(props.known)} known records
    </span>
  );
}
